import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Container,
  Typography,
  useTheme,
} from "@mui/material";
import LoginForm from "../components/LoginForm";
import loginAnimation from "../lotties/login-lottie.json";
import Lottie from "lottie-react";
import { LoginFormData } from "../components/LoginForm";
import { authService } from "../services/auth/authService";
import { useSnackbar } from "../context/SnackbarContext";
import { useUser } from "../context/UserConext";
import { decodeJWT } from "../utils/jwt";
import { useNavigate, useSearchParams } from "react-router-dom";
import ChangeCompanyModal from "../components/ChangeCompanyModal";
import { userService } from "../services/user/userService";
import { CompanyClientResponse } from "../types/api.types";

const LoginPage = () => {
  const theme = useTheme();
  const { setUser } = useUser();

  const [showModal, setShowModal] = useState(false);
  const [companyOptions, setCompanyOptions] = useState<CompanyClientResponse[]>(
    [],
  );
  const { showSnackbar } = useSnackbar();

  const [localResponse, setLocalResponse] = useState<any | null>(null);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [isLoading, setIsLoading] = useState(false);

  // Check for tokenExpired query param
  useEffect(() => {
    const tokenExpired = searchParams.get("tokenExpired");
    if (tokenExpired === "true") {
      showSnackbar(
        "Tu sesión ha expirado. Por favor, inicia sesión nuevamente.",
        "warning",
        5000,
      );
    }
  }, [searchParams, showSnackbar]);

  const onHandleSubmit = async (values: LoginFormData): Promise<void> => {
    try {
      setIsLoading(true);
      const response = await authService.login(values);
      const decoded = decodeJWT(response.token);
      if (decoded?.rol === "Client") {
        setShowModal(true);
        onGetCompanies(response.token);
        setLocalResponse(response);
        return;
      }
      setUser({
        user: {
          ...decoded,
          token: response.token,
        },
      });
      navigate("/home");
    } catch (error: any) {
      showSnackbar(
        error.response?.data?.message || "Error al iniciar sesión",
        "error",
        4000,
      );
    } finally {
      setIsLoading(false);
    }
  };

  const onGetCompanies = async (token: string) => {
    const response = await userService.getCompanies(token);
    setCompanyOptions(response);
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
        width: "100vw",
        position: "relative",
        overflow: "hidden",
        background: `linear-gradient(
          135deg,
          ${theme.palette.secondary.main},
          ${theme.palette.primary.main},
          ${theme.palette.secondary.main}
        )`,
        backgroundSize: "200% 200%",
        animation: "gradientShift 10s ease-in-out infinite",
        "@keyframes gradientShift": {
          "0%, 100%": {
            backgroundPosition: "0% 50%",
          },
          "50%": {
            backgroundPosition: "100% 50%",
          },
        },
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          pointerEvents: "none",
        },
      }}
    >
      <Card
        sx={{
          width: "450px",
          position: "relative",
          zIndex: 1,
          backdropFilter: "blur(10px)",
          backgroundColor: theme.palette.secondary.light,
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
          border: "1px solid rgba(255, 255, 255, 0.2)",
        }}
        elevation={0}
      >
        <CardContent>
          <Box
            sx={{
              height: 200,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Lottie
              style={{ width: 300, height: 300 }}
              animationData={loginAnimation}
              loop={false}
            />
          </Box>
          <LoginForm isLoading={isLoading} onSubmit={onHandleSubmit} />
        </CardContent>
      </Card>
      <ChangeCompanyModal
        showModal={showModal}
        setShowModal={setShowModal}
        companyOptions={companyOptions}
        onSubmitProp={(data) => {
          const companyId = data.company;
          const company = companyOptions.find(
            (company) => company.clientID === companyId,
          );
          if (company) {
            const decoded = decodeJWT(localResponse?.token);
            localStorage.setItem("company", JSON.stringify(company));
            setUser({ user: { ...decoded, token: localResponse?.token } });
            navigate("/home");
          }
        }}
      />
    </Box>
  );
};

export default LoginPage;
